import { manageAccess } from '../../utils/FileApiBuilder';

type Access = 'view' | 'edit' | 'admin';

interface Entry {
  user_id: string;
  access: Access;
}

/**
 * Files and folders in the shared access side panel.
 *
 * The panel speaks per-person calls (add / remove / change access); files only
 * have `POST /files/manage-access`, which SETS the whole list. This keeps the
 * list the panel is editing and sends all of it on every change, rolling the
 * local copy back if the server refuses - so Files gets the same panel as
 * every other resource instead of its own modal (feedback round 3, S8).
 */
export const buildFileAccessAdapter = (opts: {
  itemId: string;
  itemType: 'file' | 'folder';
  initial: { user_id?: string | number; collaborator_uuid?: string; access?: string }[];
}) => {
  let list: Entry[] = opts.initial
    .map((c) => ({
      user_id: String(c.collaborator_uuid || c.user_id || ''),
      access: ((c.access as Access) || 'view'),
    }))
    .filter((c) => c.user_id);

  const commit = async (next: Entry[]) => {
    const before = list;
    list = next;
    try {
      await manageAccess({ item_id: opts.itemId, item_type: opts.itemType, collaborators: list });
    } catch (e) {
      list = before;
      throw e;
    }
  };

  return {
    addCollaborator: (_resource: string, userUuid: string, access: string) =>
      commit([...list.filter((c) => c.user_id !== userUuid), { user_id: userUuid, access: access as Access }]),
    removeCollaborator: (_resource: string, userUuid: string) =>
      commit(list.filter((c) => c.user_id !== userUuid)),
    updateAccess: (_resource: string, userUuid: string, access: string) =>
      commit(list.map((c) => (c.user_id === userUuid ? { ...c, access: access as Access } : c))),
    /** For tests. */
    current: () => list,
  };
};
